import { Ref } from 'preact';
import { useEffect, useImperativeHandle, useRef } from 'preact/hooks';
import { useFocusWithin } from '../UNSAFE_useFocusWithin';

export type FocusableHandle = {
  focus: () => void;
  blur: () => void;
};
type UseFocusWithinProps = Parameters<typeof useFocusWithin>[0];
type UseFocusableTextFieldProps = UseFocusWithinProps & {
  /**
   * flag indicating whether the field is readonly
   */
  isReadonly?: boolean;

  /**
   * An optional ref to add imperative handles
   */
  ref?: Ref<FocusableHandle>;
};

/**
 * A custom hook that handles the focus when the text field
 * is toggled between readonly and enabled
 * @typedef E represents the type of the enabled element
 * @typedef R represents the type of the readonly element
 */
export function useFocusableTextField<E extends HTMLElement, R extends HTMLElement>({
  isReadonly,
  ref = () => {},
  ...useFocusWithinProps
}: UseFocusableTextFieldProps) {
  const { focusProps, isFocused } = useFocusWithin(useFocusWithinProps);

  const previousIsFocusedRef = useRef(isFocused);
  const enabledElementRef = useRef<E>(null);
  const readonlyElementRef = useRef<R>(null);

  // Retain focus when toggling between readonly and enabled
  useEffect(() => {
    // if the readonly state is changed and previously we
    // were holding focus, set the focus to newly rendered element
    if (previousIsFocusedRef.current) {
      if (isReadonly) {
        setTimeout(() => readonlyElementRef.current?.focus());
      } else {
        setTimeout(() => enabledElementRef.current?.focus());
      }
    }
  }, [isReadonly]);

  // Keep the previousFocusRef in sync, but be sure to run this hook
  // after handling focus for the readonly toggling.
  useEffect(() => {
    previousIsFocusedRef.current = isFocused;
  }, [isFocused]);

  // Add focus and blur methods
  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        if (isReadonly) {
          readonlyElementRef.current?.focus();
        } else {
          enabledElementRef.current?.focus();
        }
      },
      blur: () => {
        if (isReadonly) {
          readonlyElementRef.current?.blur();
        } else {
          enabledElementRef.current?.blur();
        }
      }
    }),
    [isReadonly]
  );

  return {
    enabledElementRef,
    readonlyElementRef,
    isFocused,
    focusProps
  };
}
